import { Dayjs } from 'dayjs';
import moment from 'moment';
import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import {
	findRemainingAttempts,
	isScheduledOrOngoingMeeting,
	isUserAccountManager,
	isUserJobseeker,
	isUserMentor,
	isUserRecruiter,
	isUserSuperAccountManager,
	isUserAlumniConnect,
	isUserUniversityManager,
	isUserDriveAdmin,
	isUserLeadGen,
	isUserSuperLeadGen,
} from 'shared-utils';
import { LeftSectionUI } from 'src/components/dashboard/dashboardTopSection/scheduleInterviewDrawer/leftSection/LeftSection';
import {
	AvailedInterviewType,
	MeetingStatus,
	MeetingType,
	UIMeeting,
	UIRound,
	UIUser,
	UserRole,
	FilterUniversityStudentsInput,
	UserPercentileBucket,
	CreateHiringAssessmentInput,
	ApplicationFormDataDetails,
	FilterLeadGenInput,
} from 'types';

export interface UserContextOptions {
	user: UIUser | null;
	isUserDataLoading: boolean;
	hasPosition: boolean;
	userMeetings: UIMeeting[];
	userRounds: UIRound[];
	isMentorLoggedIn: boolean;
	isJobseekerLoggedIn: boolean;
	isRecruiterLoggedIn: boolean;
	isUserUniversityManagerLoggedIn: boolean;
	isAccountManagerLoggedIn: boolean;
	isAlumniConnectLoggedIn: boolean;
	isSuperAccountManagerLoggedIn: boolean;
	hasAvailedAllFreeInterviews: boolean;
	hasActiveMeeting: boolean;
	isScreeningRoundInCompleted: boolean;
	latestScheduledMeeting?: UIMeeting;
	universityStudentsFilters?: FilterUniversityStudentsInput;
	leadGenFilters?: FilterLeadGenInput;
	setUser: (user: UIUser | null) => void;
	userLatestScore?: number;
	setUserLatestScore: (score?: number) => void;
	setUserMeetings: (m: UIMeeting[]) => void;
	setUserRounds: (m: UIRound[]) => void;
	totalCompletedMeetingsCount: number;
	setTotalCompletedMeetingsCount: (val: number) => void;
	hasReschedulableMeeting: boolean;
	setHasPosition: React.Dispatch<React.SetStateAction<boolean>>;
	setIsUserDataLoading: React.Dispatch<React.SetStateAction<boolean>>;
	setHasReschedulableMeeting: React.Dispatch<React.SetStateAction<boolean>>;
	allowUserToScheduleMeeting?: boolean;
	showUpdateRoleModal: boolean;
	setShowUpdateRoleModal: React.Dispatch<React.SetStateAction<boolean>>;
	showStartAIInterviewNow: boolean;
	setShowStartAIInterviewNow: (val: boolean) => void;
	showLinkLMSProfileModal: boolean;
	setShowLinkLMSProfileModal: (val: boolean) => void;
	setShowCantLinkLMSProfileModal: (val: boolean) => void;
	showCantLinkLMSProfileModal: boolean;
	studentCode?: string;
	setStudentCode: (val: string) => void;
	showAwaitingResults: boolean;
	setShowAwaitingResults: (val: boolean) => void;
	interviewType: MeetingType | null;
	setInterviewType: React.Dispatch<React.SetStateAction<MeetingType | null>>;
	showMockTestOnly: boolean;
	setShowMockTestOnly: React.Dispatch<React.SetStateAction<boolean>>;
	setUniversityStudentsFilters: React.Dispatch<React.SetStateAction<FilterUniversityStudentsInput>>;
	setLeadGenFilters: React.Dispatch<React.SetStateAction<FilterLeadGenInput>>;
	leftSectionUI: LeftSectionUI;
	setLeftSectionUI: React.Dispatch<React.SetStateAction<LeftSectionUI>>;
	openBrowserWarningModal: boolean;
	setOpenBrowserWarningModal: React.Dispatch<React.SetStateAction<boolean>>;
	remainingAttempts: number;
	setRemainingAttempts: React.Dispatch<React.SetStateAction<number>>;
	setIsUserUniversityManagerLoggedIn: (val: boolean) => void;
	selectedMockRoundTemplate: string | null;
	setSelectedMockRoundTemplate: React.Dispatch<React.SetStateAction<string | null>>;
	userPercentileBucket: UserPercentileBucket | null;
	setUserPercentileBucket: React.Dispatch<React.SetStateAction<UserPercentileBucket | null>>;
	isDriveAdminLoggedIn: boolean;
	setIsDriveAdminLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
	isLeadGenLoggedIn: boolean;
	setIsLeadGenLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
	isSuperLeadGenLoggedIn: boolean;
	setIsSuperLeadGenLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
	selectedDrive: { label: string; value: string } | undefined;
	setSelectedDrive: React.Dispatch<React.SetStateAction<{ label: string; value: string } | undefined>>;
	bulkSelectIds: string[];
	setBulkSelectIds: React.Dispatch<React.SetStateAction<string[]>>;
	benchmarkingFormData: CreateHiringAssessmentInput;
	setBenchmarkingFormData: React.Dispatch<React.SetStateAction<CreateHiringAssessmentInput>>;
	isGuestUser: boolean;
	setIsGuestUser: React.Dispatch<React.SetStateAction<boolean>>;
	hiringAssessmentId: string | null;
	setHiringAssessmentId: React.Dispatch<React.SetStateAction<string | null>>;
	applicationFormStepsDetails: ApplicationFormDataDetails;
	setApplicationFormStepsDetails: React.Dispatch<React.SetStateAction<ApplicationFormDataDetails>>;
	currentStep: number;
	setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
	applicationFormStep: number;
	setApplicationFormStep: React.Dispatch<React.SetStateAction<number>>;
	applicationFormData: ApplicationFormDataDetails;
	setApplicationFormData: React.Dispatch<React.SetStateAction<ApplicationFormDataDetails>>;
	updateFormData: (key: string, value: string | string[] | Dayjs | Date | File | undefined) => void;
	goToNextStep: () => void;
	goToPreviousStep: () => void;
	goToNextFormStep: () => void;
	goToPreviousFormStep: () => void;
	tokenLoginErrorMessage: string | null;
	setTokenLoginErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
}
export interface UserContextProps {
	children: ReactNode;
}

const UserContext = createContext<UserContextOptions>({
	user: null,
	isUserDataLoading: false,
	hasPosition: false,
	userMeetings: [],
	userRounds: [],
	isScreeningRoundInCompleted: false,
	isMentorLoggedIn: false,
	isJobseekerLoggedIn: false,
	isRecruiterLoggedIn: false,
	isUserUniversityManagerLoggedIn: false,
	isAccountManagerLoggedIn: false,
	isSuperAccountManagerLoggedIn: false,
	isAlumniConnectLoggedIn: false,
	hasAvailedAllFreeInterviews: false,
	hasActiveMeeting: false,
	userLatestScore: 0,
	universityStudentsFilters: {} as FilterUniversityStudentsInput,
	leadGenFilters: {} as FilterLeadGenInput,
	setHasPosition: () => { },
	setUserLatestScore: () => { },
	setUser: () => { },
	setUserMeetings: () => { },
	setUserRounds: () => { },
	totalCompletedMeetingsCount: 0,
	setTotalCompletedMeetingsCount: () => { },
	hasReschedulableMeeting: false,
	setHasReschedulableMeeting: () => { },
	showUpdateRoleModal: false,
	setShowUpdateRoleModal: () => { },
	setIsUserDataLoading: () => { },
	showStartAIInterviewNow: true,
	setShowStartAIInterviewNow: () => { },
	showCantLinkLMSProfileModal: false,
	showLinkLMSProfileModal: false,
	setShowLinkLMSProfileModal: () => { },
	setShowCantLinkLMSProfileModal: () => { },
	studentCode: '',
	setStudentCode: () => { },
	showAwaitingResults: false,
	setShowAwaitingResults: () => { },
	interviewType: null,
	setInterviewType: () => { },
	showMockTestOnly: false,
	setShowMockTestOnly: () => { },
	setUniversityStudentsFilters: () => { },
	setLeadGenFilters: () => { },
	leftSectionUI: LeftSectionUI.testSteps,
	setLeftSectionUI: () => { },
	openBrowserWarningModal: false,
	setOpenBrowserWarningModal: () => { },
	remainingAttempts: 0,
	setRemainingAttempts: () => { },
	setIsUserUniversityManagerLoggedIn: () => { },
	selectedMockRoundTemplate: null,
	setSelectedMockRoundTemplate: () => { },
	userPercentileBucket: null,
	setUserPercentileBucket: () => { },
	isDriveAdminLoggedIn: false,
	setIsDriveAdminLoggedIn: () => { },
	isLeadGenLoggedIn: false,
	setIsLeadGenLoggedIn: () => { },
	isSuperLeadGenLoggedIn: false,
	setIsSuperLeadGenLoggedIn: () => { },
	selectedDrive: undefined,
	setSelectedDrive: () => { },
	bulkSelectIds: [],
	setBulkSelectIds: () => { },
	benchmarkingFormData: {} as CreateHiringAssessmentInput,
	setBenchmarkingFormData: () => { },
	isGuestUser: false,
	setIsGuestUser: () => { },
	hiringAssessmentId: null,
	setHiringAssessmentId: () => { },
	applicationFormStepsDetails: {} as ApplicationFormDataDetails,
	setApplicationFormStepsDetails: () => { },
	currentStep: 1,
	setCurrentStep: () => { },
	applicationFormStep: 1,
	setApplicationFormStep: () => { },
	applicationFormData: {} as ApplicationFormDataDetails,
	setApplicationFormData: () => { },
	updateFormData: () => { },
	goToNextStep: () => { },
	goToPreviousStep: () => { },
	goToNextFormStep: () => { },
	goToPreviousFormStep: () => { },
	tokenLoginErrorMessage: null,
	setTokenLoginErrorMessage: () => { },
});

export const UserContextProvider = ({ children }: UserContextProps) => {
	const [user, setUser] = React.useState<UIUser | null>(null);
	const [isUserDataLoading, setIsUserDataLoading] = React.useState<boolean>(true);
	const [hasPosition, setHasPosition] = React.useState(false);
	const [userLatestScore, setUserLatestScore] = React.useState<number>();
	const [userMeetings, setUserMeetings] = React.useState<UIMeeting[]>([]);
	const [userRounds, setUserRounds] = React.useState<UIRound[]>([]);
	const [isMentorLoggedIn, setIsMentorLoggedIn] = React.useState<boolean>(false);
	const [isAlumniConnectLoggedIn, setIsAlumniConnectLoggedIn] = React.useState<boolean>(false);
	const [isRecruiterLoggedIn, setIsRecruiterLoggedIn] = React.useState<boolean>(false);
	const [isUserUniversityManagerLoggedIn, setIsUserUniversityManagerLoggedIn] = React.useState<boolean>(false);
	const [isAccountManagerLoggedIn, setIsAccountManagerLoggedIn] = React.useState<boolean>(false);
	const [isSuperAccountManagerLoggedIn, setIsSuperAccountManagerLoggedIn] = React.useState<boolean>(false);
	const [isDriveAdminLoggedIn, setIsDriveAdminLoggedIn] = React.useState<boolean>(false);
	const [isJobseekerLoggedIn, setIsJobseekerLoggedIn] = React.useState<boolean>(false);
	const [isLeadGenLoggedIn, setIsLeadGenLoggedIn] = React.useState<boolean>(false);
	const [isSuperLeadGenLoggedIn, setIsSuperLeadGenLoggedIn] = React.useState<boolean>(false);
	const [totalCompletedMeetingsCount, setTotalCompletedMeetingsCount] = React.useState<number>(0);
	const [hasAvailedAllFreeInterviews, setHasAvailedAllFreeInterviews] = React.useState<boolean>(false);
	const [hasActiveMeeting, setHasActiveMeeting] = React.useState<boolean>(false);
	const [hasReschedulableMeeting, setHasReschedulableMeeting] = React.useState<boolean>(false);
	const [isScreeningRoundInCompleted, setIsScreeningRoundInCompleted] = React.useState<boolean>(false);
	const [latestScheduledMeeting, setLatestScheduledMeeting] = React.useState<UIMeeting>();
	const [showStartAIInterviewNow, setShowStartAIInterviewNow] = React.useState<boolean>(true);
	const [showCantLinkLMSProfileModal, setShowCantLinkLMSProfileModal] = React.useState<boolean>(false);
	const [showLinkLMSProfileModal, setShowLinkLMSProfileModal] = React.useState<boolean>(false);
	const [studentCode, setStudentCode] = React.useState<string>();
	const [showUpdateRoleModal, setShowUpdateRoleModal] = React.useState<boolean>(false);
	const [showAwaitingResults, setShowAwaitingResults] = React.useState<boolean>(false);
	const [showMockTestOnly, setShowMockTestOnly] = React.useState<boolean>(false);
	const [interviewType, setInterviewType] = React.useState<MeetingType | null>(null);
	const [userPercentileBucket, setUserPercentileBucket] = React.useState<UserPercentileBucket | null>(null);
	const [universityStudentsFilters, setUniversityStudentsFilters] = React.useState<FilterUniversityStudentsInput>(
		{} as FilterUniversityStudentsInput,
	);
	const [leadGenFilters, setLeadGenFilters] = React.useState<FilterLeadGenInput>({} as FilterLeadGenInput);
	const [leftSectionUI, setLeftSectionUI] = useState<LeftSectionUI>(LeftSectionUI.testSteps);
	const [openBrowserWarningModal, setOpenBrowserWarningModal] = React.useState<boolean>(false);
	const [remainingAttempts, setRemainingAttempts] = React.useState<number>(0);
	const [selectedMockRoundTemplate, setSelectedMockRoundTemplate] = useState<string | null>(null);
	const [selectedDrive, setSelectedDrive] = useState<{ label: string; value: string }>();
	const [bulkSelectIds, setBulkSelectIds] = useState<string[]>([]);
	const [benchmarkingFormData, setBenchmarkingFormData] = useState<CreateHiringAssessmentInput>(
		{} as CreateHiringAssessmentInput,
	);
	const [isGuestUser, setIsGuestUser] = useState<boolean>(false);
	const [hiringAssessmentId, setHiringAssessmentId] = useState<string | null>(null);
	const [applicationFormStepsDetails, setApplicationFormStepsDetails] = useState<ApplicationFormDataDetails>(
		{} as ApplicationFormDataDetails,
	);
	const [currentStep, setCurrentStep] = useState<number>(1);
	const [applicationFormStep, setApplicationFormStep] = useState<number>(1);
	const [applicationFormData, setApplicationFormData] = useState<ApplicationFormDataDetails>({
		educationQualification: null,
		educationDegree: null,
		experience: null,
		passingYear12Th: null,
		jobType: null,
		skillsAndInterests: null,
		driveLocation: null,
		resume: null,
	});

	const [tokenLoginErrorMessage, setTokenLoginErrorMessage] = useState<string | null>(null);

	const updateFormData = (key: string, value: string | string[] | Dayjs | Date | File | undefined) => {
		setApplicationFormData(prev => ({
			...prev,
			[key]: value,
		}));
	};

	const goToNextStep = () => {
		setCurrentStep(prev => prev + 1);
	};

	const goToPreviousStep = () => {
		setCurrentStep(prev => (prev > 1 ? prev - 1 : prev));
	};

	const goToNextFormStep = () => {
		setApplicationFormStep(prev => prev + 1);
	};

	const goToPreviousFormStep = () => {
		setApplicationFormStep(prev => (prev > 1 ? prev - 1 : prev));
	};

	useEffect(() => {
		setIsMentorLoggedIn(isUserMentor(user?.role as UserRole));
		setIsAlumniConnectLoggedIn(isUserAlumniConnect(user?.role as UserRole));
		setIsJobseekerLoggedIn(isUserJobseeker(user?.role as UserRole));
		setIsRecruiterLoggedIn(isUserRecruiter(user?.role as UserRole));
		setIsUserUniversityManagerLoggedIn(isUserUniversityManager(user?.role as UserRole));
		setIsAccountManagerLoggedIn(isUserAccountManager(user?.role as UserRole));
		setIsSuperAccountManagerLoggedIn(isUserSuperAccountManager(user?.role as UserRole));
		setIsDriveAdminLoggedIn(isUserDriveAdmin(user?.role as UserRole));
		setIsLeadGenLoggedIn(isUserLeadGen(user?.role as UserRole));
		setIsSuperLeadGenLoggedIn(isUserSuperLeadGen(user?.role as UserRole));
		setHasAvailedAllFreeInterviews(Number(user?.availedFreeInterviews.length) >= 2);
	}, [user]);

	useEffect(() => {
		setHasActiveMeeting(!!userMeetings.find(m => isScheduledOrOngoingMeeting(m.meetingStatus)));
		setHasReschedulableMeeting(
			!!userMeetings.find(
				m => m.meetingStatus === MeetingStatus.SCHEDULED && moment().isBefore(moment(m.startTime).subtract(1, 'hour')),
			),
		);
		setLatestScheduledMeeting(userMeetings.find(m => isScheduledOrOngoingMeeting(m.meetingStatus)));
	}, [userMeetings]);

	useEffect(() => {
		setIsScreeningRoundInCompleted(!user?.profile?.gradYear || !user?.profile?.currentWorkingStatus);
	}, [user?.profile.gradYear, user?.profile.currentWorkingStatus]);

	useEffect(() => {
		const isGuest = true;
		setIsGuestUser(isGuest);
	}, [setIsGuestUser]);

	const userAvailableRounds = userRounds.filter(
		r =>
			findRemainingAttempts(r.maxAttempts, r.meetings) &&
			(showMockTestOnly ? r.isMock : !r?.isMock) &&
			(!interviewType ||
				(interviewType === MeetingType.Assessment
					? interviewType === r.meetingType
					: r.meetingType !== MeetingType.Assessment)),
	);

	const numberOfUserNoShows = userMeetings.filter(m => m.meetingStatus === MeetingStatus.JOBSEEKERNOSHOW).length;
	const allowNoShowSchedule =
		numberOfUserNoShows === 1 &&
		!!user?.profile.videoResume &&
		!user.availedFreeInterviews.some(a => a.interviewType === AvailedInterviewType.OneNoShow);

	const allowUserToScheduleMeeting =
		// user?.flags.isMasaiStudent &&
		!hasActiveMeeting &&
		!hasReschedulableMeeting &&
		(allowNoShowSchedule ||
			userAvailableRounds.length > 0 ||
			!user?.availedFreeInterviews.some(a => a.interviewType === AvailedInterviewType.First) ||
			(user.flags.hasCompletedPrepPlans &&
				!user?.availedFreeInterviews.some(a => a.interviewType === AvailedInterviewType.Second)));

	return (
		<UserContext.Provider
			value={{
				user,
				userMeetings,
				userRounds,
				isMentorLoggedIn,
				isUserDataLoading,
				setIsUserDataLoading,
				isJobseekerLoggedIn,
				isRecruiterLoggedIn,
				isUserUniversityManagerLoggedIn,
				isAccountManagerLoggedIn,
				isSuperAccountManagerLoggedIn,
				isAlumniConnectLoggedIn,
				hasAvailedAllFreeInterviews,
				hasActiveMeeting,
				latestScheduledMeeting,
				isScreeningRoundInCompleted,
				userLatestScore,
				universityStudentsFilters,
				leadGenFilters,
				setUserLatestScore,
				setUser,
				setUserMeetings,
				setUserRounds,
				totalCompletedMeetingsCount,
				setTotalCompletedMeetingsCount,
				hasReschedulableMeeting,
				setHasReschedulableMeeting,
				allowUserToScheduleMeeting,
				hasPosition,
				setHasPosition,
				showUpdateRoleModal,
				setShowUpdateRoleModal,
				showStartAIInterviewNow,
				setShowStartAIInterviewNow,
				showCantLinkLMSProfileModal,
				showLinkLMSProfileModal,
				setShowCantLinkLMSProfileModal,
				setShowLinkLMSProfileModal,
				studentCode,
				showAwaitingResults,
				setShowAwaitingResults,
				setStudentCode,
				interviewType,
				setInterviewType,
				showMockTestOnly,
				setShowMockTestOnly,
				setUniversityStudentsFilters,
				setLeadGenFilters,
				leftSectionUI,
				setLeftSectionUI,
				openBrowserWarningModal,
				setOpenBrowserWarningModal,
				remainingAttempts,
				setRemainingAttempts,
				setIsUserUniversityManagerLoggedIn,
				selectedMockRoundTemplate,
				setSelectedMockRoundTemplate,
				userPercentileBucket,
				setUserPercentileBucket,
				isDriveAdminLoggedIn,
				setIsDriveAdminLoggedIn,
				isLeadGenLoggedIn,
				setIsLeadGenLoggedIn,
				isSuperLeadGenLoggedIn,
				setIsSuperLeadGenLoggedIn,
				selectedDrive,
				setSelectedDrive,
				bulkSelectIds,
				setBulkSelectIds,
				benchmarkingFormData,
				setBenchmarkingFormData,
				isGuestUser,
				setIsGuestUser,
				hiringAssessmentId,
				setHiringAssessmentId,
				applicationFormStepsDetails,
				setApplicationFormStepsDetails,
				currentStep,
				setCurrentStep,
				applicationFormStep,
				setApplicationFormStep,
				applicationFormData,
				setApplicationFormData,
				updateFormData,
				goToNextStep,
				goToPreviousStep,
				goToNextFormStep,
				goToPreviousFormStep,
				tokenLoginErrorMessage,
				setTokenLoginErrorMessage,
			}}
		>
			{children}
		</UserContext.Provider>
	);
};

export const useUserContext = () => {
	const context = useContext(UserContext);
	return context;
};
